import { useState } from "react";
import { gossipItems, hotTags, type GossipItem } from "@/mocks/gossip";

const TAG_STYLE: Record<string, string> = {
  hot: "bg-accent-500 text-background-50",
  new: "bg-secondary-500 text-background-50",
  deep: "bg-primary-500 text-background-50",
};

const TAG_LABEL: Record<string, string> = {
  hot: "热议",
  new: "新料",
  deep: "深扒",
};

function GossipCard({ item }: { item: GossipItem }) {
  const [liked, setLiked] = useState(false);
  const featured = item.rank === 1;

  return (
    <article
      className={`group rounded-2xl overflow-hidden bg-background-50 border border-background-200 flex flex-col ${
        featured ? "md:flex-row" : ""
      }`}
    >
      <div
        className={`relative overflow-hidden shrink-0 ${
          featured ? "md:w-[58%] md:min-h-[340px]" : "w-full"
        }`}
      >
        <a href="#feed" className="block cursor-pointer">
          <img
            src={item.cover}
            alt={item.title}
            title={item.title}
            className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
              featured ? "h-56 md:h-full" : "h-56 md:h-64"
            }`}
          />
        </a>
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent"></div>
        <span
          className={`absolute top-4 left-4 inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full font-medium ${TAG_STYLE[item.tagType]}`}
        >
          {TAG_LABEL[item.tagType]}
        </span>
        <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 text-background-50 text-xs px-3 py-1 rounded-full bg-black/40 backdrop-blur">
          <i className="ri-fire-line text-secondary-300"></i> 热度 {item.heat}
        </span>
      </div>

      <div className={`flex flex-col flex-1 p-5 md:p-6 ${featured ? "md:justify-center" : ""}`}>
        <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
          <span className="text-primary-600 font-medium">No.{item.rank}</span>
          <span className="w-1 h-1 rounded-full bg-background-300"></span>
          <span>{item.time}</span>
        </div>
        <h3 className="font-heading font-bold text-foreground-950 leading-snug text-base md:text-lg mb-2">
          <a href="#feed" className="hover:text-primary-600 transition-colors">
            {item.title}
          </a>
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-3">{item.excerpt}</p>

        <div className="mt-auto flex items-center gap-2 pt-3">
          <button
            onClick={() => setLiked((v) => !v)}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors cursor-pointer ${
              liked
                ? "bg-accent-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:text-accent-500"
            }`}
          >
            <i className={`${liked ? "ri-fire-fill" : "ri-fire-line"} text-sm`}></i>
            实锤 {liked ? item.heat : item.comments}
          </button>
          <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-700">
            <i className="ri-chat-3-line text-sm"></i> {item.comments} 围观
          </span>
          <button className="ml-auto w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:text-primary-500 hover:bg-background-100 cursor-pointer">
            <i className="ri-share-forward-line text-sm"></i>
          </button>
        </div>
      </div>
    </article>
  );
}

export default function GossipFeed() {
  const [activeTag, setActiveTag] = useState("全部");
  const featured = gossipItems[0];
  const rest = gossipItems.slice(1);

  return (
    <section id="feed" className="relative py-14 md:py-20 bg-background-100/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <span className="inline-flex items-center gap-1.5 text-primary-600 text-xs font-medium tracking-wide mb-3">
              <i className="ri-flashlight-fill"></i> 编辑精选
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
              今日瓜榜
            </h2>
          </div>
          <p className="text-sm text-foreground-600 max-w-sm md:text-right">
            每条上榜线索均经匿名通道投递并完成初步复核，配图仅为示意。
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6 md:mb-8">
          {["全部", ...hotTags.slice(0, 5)].map((t) => (
            <button
              key={t}
              onClick={() => setActiveTag(t)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                activeTag === t
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:text-foreground-950 border border-background-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-5 md:gap-6">
          <GossipCard item={featured} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
            {rest.map((item) => (
              <GossipCard key={item.id} item={item} />
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center">
          <button className="inline-flex items-center gap-2 rounded-full bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 text-sm px-6 py-3 transition-colors cursor-pointer">
            换一屏，看看更多新鲜瓜 <i className="ri-refresh-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}